import React, { useEffect, useState } from 'react';
import { FileDown, FileText, Loader2, Plus, Trash2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { formatApiError } from '../lib/apiError';

export const APP_FIELDS = ['phone_home', 'phone_work', 'service_corps', 'service_number', 'enlistment_date', 'discharge_date', 'trade', 'decorations',
  'overseas_service', 'overseas_country', 'occupation', 'signed_name', 'signed_date', 'nominated_by', 'nominated_date',
  'receipt_no', 'president_name', 'president_date', 'secretary_name', 'secretary_date', 'application_status', 'postings'];

export const emptyApplication = () => ({ ...Object.fromEntries(APP_FIELDS.map((k) => [k, ''])), overseas_service: false, postings: [] });

function F({ label, k, app, set, span, hint, type }) {
  return (
    <label className={`block ${span ? 'sm:col-span-2' : ''}`}>
      <span className="block text-xs mb-1 font-title" style={{ color: 'var(--olive)' }}>{label}</span>
      <Input type={type || 'text'} data-testid={`member-app-${k.replace(/_/g, '-')}`} value={app[k] || ''} onChange={(e) => set(k, e.target.value)} placeholder={hint || ''} />
    </label>
  );
}

function Postings({ postings, onChange }) {
  const setP = (i, k, v) => onChange(postings.map((p, j) => (j === i ? { ...p, [k]: v } : p)));
  return (
    <div className="sm:col-span-2" data-testid="member-app-postings">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-title" style={{ color: 'var(--olive)' }}>Unit postings</span>
        <button type="button" onClick={() => onChange([...postings, { unit: '', from_date: '', to_date: '' }])} data-testid="member-app-posting-add" className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded font-title" style={{ background: 'var(--navy)', color: 'var(--cream)' }}><Plus size={12} /> Add posting</button>
      </div>
      {postings.length === 0 && <p className="text-xs" style={{ color: 'var(--olive)' }}>No postings recorded.</p>}
      {postings.map((p, i) => (
        <div key={i} className="grid grid-cols-[1fr_110px_110px_28px] gap-2 mb-1.5">
          <Input value={p.unit || ''} onChange={(e) => setP(i, 'unit', e.target.value)} placeholder="Unit" className="h-8" data-testid={`member-app-posting-unit-${i}`} />
          <Input value={p.from_date || ''} onChange={(e) => setP(i, 'from_date', e.target.value)} placeholder="From" className="h-8" data-testid={`member-app-posting-from-${i}`} />
          <Input value={p.to_date || ''} onChange={(e) => setP(i, 'to_date', e.target.value)} placeholder="To" className="h-8" data-testid={`member-app-posting-to-${i}`} />
          <button type="button" onClick={() => onChange(postings.filter((_, j) => j !== i))} title="Remove" data-testid={`member-app-posting-del-${i}`}><Trash2 size={14} style={{ color: '#8a1f11' }} /></button>
        </div>
      ))}
    </div>
  );
}

// All Membership Application form fields, editable on the member record, with a populated-PDF download.
export default function MemberApplicationSection({ user, app, setApp, memberForm, membershipNo, setMembershipNo }) {
  const { adminMemberApplicationForm, adminMemberApplicationPdf } = useAuth();
  const [meta, setMeta] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const set = (k, v) => setApp((a) => ({ ...a, [k]: v }));

  useEffect(() => {
    let alive = true;
    adminMemberApplicationForm(user.user_id).then((r) => {
      if (!alive) return;
      setMeta(r);
      const f = r.form || {};
      setApp((a) => {
        const next = { ...a };
        APP_FIELDS.forEach((k) => { if (k === 'application_status') { if (!next[k]) next[k] = f.status || ''; } else if (next[k] === '' || next[k] == null || (Array.isArray(next[k]) && !next[k].length)) next[k] = f[k] ?? next[k]; });
        return next;
      });
      if (!membershipNo && f.membership_no) setMembershipNo(f.membership_no);
    }).catch(() => { if (alive) setMeta({ error: true }); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.user_id]);

  const pdf = async () => {
    setPdfBusy(true);
    try {
      await adminMemberApplicationPdf(user.user_id, { application: app, member: { ...memberForm, membership_no: membershipNo } }, `application-${memberForm.last_name || ''}-${memberForm.first_name || ''}.pdf`);
      toast.success('Application form PDF downloaded with the entered details.');
    } catch (e) { toast.error(formatApiError(e, 'Could not build the PDF.')); }
    setPdfBusy(false);
  };

  return (
    <div data-testid="member-app-section">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <h4 className="font-title text-sm flex items-center gap-2" style={{ color: 'var(--navy)' }}><FileText size={15} /> Membership application form</h4>
        <button type="button" onClick={pdf} disabled={pdfBusy} data-testid="member-app-pdf" className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded font-title border" style={{ borderColor: 'var(--navy)', color: 'var(--navy)', opacity: pdfBusy ? 0.6 : 1 }}>
          {pdfBusy ? <Loader2 size={13} className="animate-spin" /> : <FileDown size={13} />} Application form PDF (populated)
        </button>
      </div>
      <p className="text-xs mb-3" style={{ color: 'var(--olive)' }} data-testid="member-app-source">
        {meta == null ? 'Loading application details…' : meta.error ? 'Could not load the submitted application.' : meta.latest_application_id
          ? `Pre-filled from the application submitted ${meta.latest_submitted_at ? new Date(meta.latest_submitted_at).toLocaleDateString() : ''}${meta.has_saved_edits ? ' (with your saved edits)' : ''}. Personal and next-of-kin details come from the fields above.`
          : 'No submitted application on file — fill in the details below. Personal and next-of-kin details come from the fields above.'}
      </p>
      <div className="grid sm:grid-cols-2 gap-3 rounded p-3" style={{ background: 'rgba(21,56,91,0.04)' }}>
        <F label="Phone (home)" k="phone_home" app={app} set={set} />
        <F label="Phone (work)" k="phone_work" app={app} set={set} />
        <F label="Service / Corps" k="service_corps" app={app} set={set} hint="e.g. Army / RAE" />
        <F label="Service number" k="service_number" app={app} set={set} />
        <F label="Enlistment date" k="enlistment_date" app={app} set={set} hint="e.g. 1978-03-01" />
        <F label="Discharge date" k="discharge_date" app={app} set={set} hint="e.g. 1985-11-30" />
        <F label="Trade" k="trade" app={app} set={set} span />
        <label className="block sm:col-span-2">
          <span className="block text-xs mb-1 font-title" style={{ color: 'var(--olive)' }}>Decorations &amp; awards</span>
          <Textarea data-testid="member-app-decorations" value={app.decorations || ''} onChange={(e) => set('decorations', e.target.value)} className="h-16" />
        </label>
        <Postings postings={app.postings || []} onChange={(p) => set('postings', p)} />
        <label className="inline-flex items-center gap-2 text-sm sm:col-span-2"><input type="checkbox" checked={!!app.overseas_service} onChange={(e) => set('overseas_service', e.target.checked)} data-testid="member-app-overseas" /> Overseas service</label>
        <F label="Country (overseas service)" k="overseas_country" app={app} set={set} />
        <F label="Current occupation" k="occupation" app={app} set={set} />
        <F label="Signed (typed name)" k="signed_name" app={app} set={set} />
        <F label="Signed date" k="signed_date" app={app} set={set} hint="YYYY-MM-DD" />
        <F label="Nominated by" k="nominated_by" app={app} set={set} />
        <F label="Nomination date" k="nominated_date" app={app} set={set} hint="YYYY-MM-DD" />
      </div>
      <div className="grid sm:grid-cols-3 gap-3 rounded p-3 mt-3" style={{ background: 'rgba(122,31,20,0.05)' }} data-testid="member-app-committee">
        <div className="sm:col-span-3 text-xs font-title" style={{ color: 'var(--maroon)' }}>Committee use only</div>
        <label className="block">
          <span className="block text-xs mb-1 font-title" style={{ color: 'var(--olive)' }}>Membership No</span>
          <Input data-testid="member-app-membership-no" value={membershipNo || ''} onChange={(e) => setMembershipNo(e.target.value)} />
        </label>
        <F label="Receipt No" k="receipt_no" app={app} set={set} />
        <F label="Membership decision" k="application_status" app={app} set={set} hint="e.g. accepted / declined" />
        <F label="President" k="president_name" app={app} set={set} />
        <F label="President date" k="president_date" app={app} set={set} hint="YYYY-MM-DD" />
        <div />
        <F label="Secretary" k="secretary_name" app={app} set={set} />
        <F label="Secretary date" k="secretary_date" app={app} set={set} hint="YYYY-MM-DD" />
      </div>
    </div>
  );
}
